import { useMemo, useState } from "react";
import moment from "moment";
import { IMatchDetailDTO } from "../../../models/IMatch";
import { StageDetails } from "../../../types/tournament";
import Bracket from "../../../components/Bracket";
import { formatSideName, isAmericanoFormat } from "../../../utils/methods";
import { withBracketPlaceholders } from "../bracketPreview";
import { Button, Card, DatePicker, EmptyState, Input, Loader, Select, TableWrap, Tabs, Tag, Toolbar } from "../../../ui";
import StepHeader from "./StepHeader";

const roundNumber = (round?: string | null) => {
  const m = /^Round (\d+)$/.exec(round ?? "");
  return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
};

interface Props {
  stage?: StageDetails;
  matches: IMatchDetailDTO[];
  previewLoading: boolean;
  activeSchedulingRound?: { name: string; order: number };
  setActiveSchedulingRound: (v: { name: string; order: number }) => void;
  matchChangeHandler: (matchId: string, field: string, value: string | Date | null) => void;
  getDependentMatch: (matchId: string, isThirdPlace: boolean) => string;
}

const MatchSchedulingStep = ({
  stage, matches, previewLoading, matchChangeHandler, getDependentMatch,
}: Props) => {
  const [filterDate, setFilterDate] = useState("");
  const [filterTeam, setFilterTeam] = useState("");
  const [autoStart, setAutoStart] = useState<Date | null>(null);
  const [autoInterval, setAutoInterval] = useState<number>(30);

  const uniqueTeams = useMemo(() => {
    const teams = new Set<string>();
    matches.forEach((m) => {
      if (m.homeTeamName) teams.add(m.homeTeamName);
      if (m.awayTeamName) teams.add(m.awayTeamName);
    });
    return [...teams].sort();
  }, [matches]);

  const uniqueDates = useMemo(() => {
    const dates = new Set<string>();
    matches.forEach((m) => {
      if (m.startTime) dates.add(moment(m.startTime).format("YYYY-MM-DD"));
    });
    return [...dates].sort();
  }, [matches]);

  const filtered = useMemo(() => {
    let list = matches;
    if (filterDate) list = list.filter((m) => m.startTime && moment(m.startTime).format("YYYY-MM-DD") === filterDate);
    if (filterTeam) {
      list = list.filter((m) => m.homeTeamName === filterTeam || m.awayTeamName === filterTeam);
    }
    return list;
  }, [matches, filterDate, filterTeam]);

  const activeFilterCount = [filterDate, filterTeam].filter(Boolean).length;

  // Knockout: the draw is reviewed as a bracket before creating; the table stays one tab away.
  const knockout = stage?.formatName === "Knockout";
  const [view, setView] = useState<"bracket" | "matches">("bracket");
  const bracketMatches = useMemo(() => (knockout ? withBracketPlaceholders(matches) : []), [knockout, matches]);

  // Americano/Mexicano: a round is played simultaneously across courts, so matches
  // are grouped per round with ONE date & time shared by the whole round.
  const americano = isAmericanoFormat(stage?.formatName);
  const rounds = useMemo(() => {
    if (!americano) return [];
    const byRound = new Map<string, IMatchDetailDTO[]>();
    filtered.forEach((m) => {
      const key = m.round || "Round ?";
      if (!byRound.has(key)) byRound.set(key, []);
      byRound.get(key)!.push(m);
    });
    return [...byRound.entries()].sort((a, b) => roundNumber(a[0]) - roundNumber(b[0]));
  }, [americano, filtered]);

  // Move a whole round: every match keeps its offset from the round's first slot,
  // so court waves (more matches than courts) survive a time change.
  const setRoundTime = (roundMatches: IMatchDetailDTO[], date: Date | null) => {
    const base = moment(date).valueOf();
    const times = roundMatches.filter((m) => m.startTime).map((m) => new Date(m.startTime!).getTime());
    const oldBase = times.length > 0 ? Math.min(...times) : null;
    roundMatches.forEach((m) => {
      const offset = oldBase !== null && m.startTime ? new Date(m.startTime).getTime() - oldBase : 0;
      matchChangeHandler(m.id, "startTime", new Date(base + offset).toISOString());
    });
  };

  // Fill every round from a start time, each round starting `autoInterval` minutes later.
  const applyAutoFill = () => {
    if (!autoStart) return;
    rounds.forEach(([, roundMatches], idx) => {
      const t = new Date(autoStart.getTime() + idx * autoInterval * 60000);
      roundMatches.forEach((m) => matchChangeHandler(m.id, "startTime", t.toISOString()));
    });
  };

  const minDate = stage?.startDate ? new Date(moment(stage.startDate).format("YYYY-MM-DD")) : undefined;
  const maxDate = stage?.endDate ? new Date(moment(stage.endDate).format("YYYY-MM-DD")) : undefined;
  const noMatches = <EmptyState compact className="empty-state" title="No matches found" />;

  return (
    <div className="cs-step match-scheduling-container">
      <StepHeader
        title="Match Scheduling"
        subtitle={
          knockout
            ? "Review the bracket, then set dates, times, and venues under Matches. Changes are saved as draft."
            : "Set dates, times, and venues. Changes are saved as draft."
        }
      />
      {previewLoading ? (
        <Loader minHeight={200} className="preview-loader-container" />
      ) : (
        <>
          {knockout && (
            <Tabs
              className="tabs-list"
              ariaLabel="Match Scheduling"
              options={[
                { value: "bracket", label: "Bracket" },
                { value: "matches", label: "Matches" },
              ]}
              value={view}
              onChange={(v) => setView(v as "bracket" | "matches")}
            />
          )}
          {knockout && view === "bracket" ? (
            <div className="bracket-preview">
              <Bracket matches={bracketMatches} readOnly />
            </div>
          ) : (
          <>
          <Toolbar className="match-filters" actions={<span className="match-count">{filtered.length} match{filtered.length !== 1 ? "es" : ""}</span>}>
            <Select size="sm" aria-label="All Dates" value={filterDate} onChange={(e) => setFilterDate(e.target.value)}>
              <option value="">All Dates</option>
              {uniqueDates.map((d) => (
                <option key={d} value={d}>{moment(d).format("ddd, DD MMM")}</option>
              ))}
            </Select>
            <Select size="sm" aria-label="All Teams" value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)}>
              <option value="">All Teams</option>
              {uniqueTeams.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Select>
            {activeFilterCount > 0 && (
              <Button
                size="sm"
                variant="ghost"
                className="clear-filters"
                onClick={() => { setFilterDate(""); setFilterTeam(""); }}
              >
                Clear
              </Button>
            )}
          </Toolbar>

          {americano && (
            <Card padding="sm" className="americano-autofill">
              <div className="autofill-row">
                <span className="autofill-label">Set all round times:</span>
                <div className="autofill-start">
                  <DatePicker
                    mode="datetime"
                    timeIntervals={5}
                    size="sm"
                    aria-label="Start date & time"
                    placeholderText="Start date & time"
                    dateFormat="dd MMM yyyy - hh:mm aa"
                    minDate={minDate}
                    maxDate={maxDate}
                    selected={autoStart}
                    onChange={(d) => setAutoStart(d)}
                  />
                </div>
                <span className="autofill-gap">every</span>
                <Input
                  inputSize="sm"
                  block={false}
                  className="autofill-interval"
                  aria-label="every min"
                  type="number"
                  min={1}
                  value={autoInterval}
                  onChange={(e) => setAutoInterval(Math.max(1, parseInt(e.target.value, 10) || 0))}
                />
                <span className="autofill-gap">min</span>
                <Button size="sm" className="autofill-btn" disabled={!autoStart} onClick={applyAutoFill}>
                  Apply to all rounds
                </Button>
              </div>
            </Card>
          )}

          {americano ? (
            <div className="americano-rounds">
              {rounds.length === 0 ? (
                <Card>{noMatches}</Card>
              ) : (
                rounds.map(([roundName, roundMatches]) => (
                  <Card
                    key={roundName}
                    padding="none"
                    className="round-section"
                    title={roundName}
                    subtitle={
                      <span className="round-count">
                        {roundMatches.length} match{roundMatches.length !== 1 ? "es" : ""} played at the same time
                      </span>
                    }
                    actions={
                      <div className="round-time">
                        <DatePicker
                          mode="datetime"
                          timeIntervals={5}
                          size="sm"
                          aria-label={`${roundName} date & time`}
                          placeholderText="Set round date & time"
                          dateFormat="dd MMM yyyy - hh:mm aa"
                          minDate={minDate}
                          maxDate={maxDate}
                          selected={roundMatches[0]?.startTime ? new Date(roundMatches[0].startTime) : null}
                          onChange={(date) => setRoundTime(roundMatches, date)}
                        />
                      </div>
                    }
                  >
                    <div className="cs-table-scroll">
                      <table className="ui-table scheduled-matches-table">
                        <thead>
                          <tr>
                            <th>Side 1</th>
                            <th>Side 2</th>
                            <th>Time</th>
                            <th>Court / Venue</th>
                            <th>Format</th>
                          </tr>
                        </thead>
                        <tbody>
                          {roundMatches.map((match) => (
                            <tr className="match-row" key={match.id}>
                              <td className="col-team">
                                <span className="team-name">{formatSideName(match.homeTeamName, match.homeTeam2Name)}</span>
                              </td>
                              <td className="col-team">
                                <span className="team-name">{formatSideName(match.awayTeamName, match.awayTeam2Name)}</span>
                              </td>
                              <td className="match-time">
                                {match.startTime ? moment(match.startTime).format("hh:mm A") : "—"}
                              </td>
                              <td className="col-venue">
                                <Input
                                  inputSize="sm"
                                  aria-label="Court"
                                  value={match.venue ?? ""}
                                  placeholder="Court"
                                  onChange={(e) => matchChangeHandler(match.id, "venue", e.target.value)}
                                />
                              </td>
                              <td className="fixed-format">
                                Game to {stage?.americanoRules?.pointsPerMatch ?? "-"} pts
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                ))
              )}
            </div>
          ) : (
          <TableWrap className="scheduled-matches">
            <table className="ui-table scheduled-matches-table">
              <thead>
                <tr>
                  <th>Team 1</th>
                  <th>Team 2</th>
                  <th>Date & Time</th>
                  <th>Venue</th>
                  <th>Format</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5}>{noMatches}</td>
                  </tr>
                ) : (
                  filtered.map((match) => (
                    <tr className="match-row" key={match.id}>
                      <td className="col-team">
                        {match.group && <Tag tone="brand" className="group-tag">G{match.group}</Tag>}
                        <span className="team-name">
                          {match.homeTeamName
                            ? formatSideName(match.homeTeamName, match.homeTeam2Name)
                            : getDependentMatch(match.parentMatch1Id!, match.round === "Third Place")}
                        </span>
                      </td>
                      <td className="col-team">
                        <span className="team-name">
                          {match.awayTeamName
                            ? formatSideName(match.awayTeamName, match.awayTeam2Name)
                            : getDependentMatch(match.parentMatch2Id!, match.round === "Third Place")}
                        </span>
                      </td>
                      <td className="col-date">
                        <DatePicker
                          mode="datetime"
                          timeIntervals={5}
                          size="sm"
                          aria-label="Date & Time"
                          placeholderText="Set date & time"
                          dateFormat="dd MMM yyyy - hh:mm aa"
                          minDate={minDate}
                          maxDate={maxDate}
                          selected={match.startTime ? new Date(match.startTime) : null}
                          onChange={(date) => {
                            const localDate = moment(date).toISOString();
                            matchChangeHandler(match.id, "startTime", localDate);
                          }}
                        />
                      </td>
                      <td className="col-venue">
                        <Input
                          inputSize="sm"
                          aria-label="Venue"
                          value={match.venue ?? ""}
                          placeholder="Venue"
                          onChange={(e) => matchChangeHandler(match.id, "venue", e.target.value)}
                        />
                      </td>
                      <td className="col-best">
                        <Select
                          size="sm"
                          block
                          aria-label="Format"
                          value={match.bestOf}
                          onChange={(e) => matchChangeHandler(match.id, "bestOf", e.target.value)}
                        >
                          <option value={1}>Best of 1</option>
                          <option value={3}>Best of 3</option>
                          <option value={5}>Best of 5</option>
                        </Select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableWrap>
          )}
          </>
          )}
        </>
      )}
    </div>
  );
};

export default MatchSchedulingStep;
